import React, { useState, useRef, useEffect } from 'react';
import {
  Play,
  CheckCircle,
  AlertTriangle,
  StopCircle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Lock,
} from 'lucide-react';

// One dropdown, one button. What actually runs is DERIVED from the
// environment, not chosen separately by the user — see the ENV_RULES table
// below. This replaces an earlier version of this page that made the person
// clicking the button choose between "Server (Jest)" and "E2E (Playwright)"
// as a top-level toggle, which required understanding the runner
// architecture just to run a test. See docs/testing/TEST_STRATEGY_COVERAGE_MAP.md §8.3
// for the underlying reasoning this still encodes:
//   - Jest (unit + integration) spins up its OWN in-memory MongoDB and never
//     makes an HTTP request to any running server — it is incapable of
//     targeting an environment, so it only ever runs on "Localhost".
//   - Playwright is a real browser hitting a real server, so it needs a
//     target — and on Prod, only specs that self-certify as read-only in
//     their own file header may run (enforced server-side too, see
//     PROD_SAFE_E2E_FILES in internal-support.controller.js).
//
// HIDE, don't disable. Anything that can't run in the selected environment is
// removed from the list rather than rendered greyed out. Greying out was the
// wrong affordance here: "Jest + Dev" isn't a restricted combination, it's a
// meaningless one, and showing 44 permanently-unclickable rows plus two alert
// banners explaining runner internals buried the 3–10 rows that could actually
// run. The counts are summarised in one line instead (see envNotes below).
// The excluded set is still computed — the run payload and the per-environment
// selection reset depend on it — it just isn't rendered.
const ENV_RULES = {
  localhost: { label: 'Localhost', jestAllowed: true, e2eProdOnly: false },
  dev: { label: 'Dev (dev.talentcrux.ai)', jestAllowed: false, e2eProdOnly: false },
  prod: { label: 'Prod (talentcrux.ai)', jestAllowed: false, e2eProdOnly: true },
};

const namespacedId = (kind, file) => `${kind}:${file}`;

const AIHealerAdmin = () => {
  const [environment, setEnvironment] = useState('localhost');
  const [isRunning, setIsRunning] = useState(false);
  const [availableTestsLoaded, setAvailableTestsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [stats, setStats] = useState({ passed: 0, failed: 0, running: 0 });

  // One unified map for BOTH runners, keyed by "jest:<file>" / "e2e:<file>".
  // Each entry: { kind, file, selected, prodSafe (e2e only), status,
  // expanded, error, totalTests, passedTests, failedTests, childTests: [] }
  const [testItems, setTestItems] = useState({});

  const activeStreamCount = useRef(0);
  const e2eAbortRef = useRef(null);
  const jestAbortRef = useRef(null);
  const retestAbortControllers = useRef({});
  const [copiedKey, setCopiedKey] = useState(null);

  // ── Load both test lists once on mount, merged into one map ─────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL || '';

    const fetchE2E = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/internal/support-requests/tests/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return {};
        const data = await res.json();
        const items = {};
        Object.entries(data.files || {}).forEach(([file, fileData]) => {
          const tests = Array.isArray(fileData) ? fileData : fileData.tests || [];
          const prodSafe = Array.isArray(fileData) ? false : !!fileData.prodSafe;
          items[namespacedId('e2e', file)] = {
            kind: 'e2e',
            file,
            prodSafe,
            selected: true,
            expanded: false,
            status: 'idle',
            error: null,
            totalTests: 0,
            passedTests: 0,
            failedTests: 0,
            childTests: tests.map((title) => ({ title, status: 'idle', error: null, traceUrl: null, videoUrl: null })),
          };
        });
        return items;
      } catch (e) {
        console.error('Failed to load E2E tests list', e);
        return {};
      }
    };

    const fetchJest = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/internal/support-requests/jest-tests/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return {};
        const data = await res.json();
        const items = {};
        (data.files || []).forEach((file) => {
          items[namespacedId('jest', file)] = {
            kind: 'jest',
            file,
            selected: true,
            expanded: false,
            status: 'idle',
            error: null,
            totalTests: 0,
            passedTests: 0,
            failedTests: 0,
            // Jest has no equivalent of Playwright's `--list` (per-file test
            // titles ahead of a run) — child rows appear as results stream in.
            childTests: [],
          };
        });
        return items;
      } catch (e) {
        console.error('Failed to load Jest tests list', e);
        return {};
      }
    };

    Promise.all([fetchE2E(), fetchJest()])
      .then(([e2eItems, jestItems]) => {
        setTestItems({ ...jestItems, ...e2eItems });
      })
      .catch((e) => setLoadError(e.message))
      .finally(() => setAvailableTestsLoaded(true));
  }, []);

  // ── What this environment can run, derived from ENV_RULES ────────────────
  // Excluded == not rendered at all. Still evaluated here because selection
  // state and the run payload must never include an excluded test.
  const isExcludedByEnv = (item) => {
    if (item.kind === 'jest') return !ENV_RULES[environment].jestAllowed;
    if (item.kind === 'e2e') return ENV_RULES[environment].e2eProdOnly && !item.prodSafe;
    return false;
  };

  // Changing the environment resets selection to "everything runnable here".
  //
  // Deselecting-only (what this did before) was fine while excluded rows stayed
  // visible and greyed, but now that they're hidden it produced a confusing
  // state: switch Localhost → Prod → Localhost and the 44 Jest rows come back
  // *unchecked*, with no visible cause. Since the load default is already
  // "all selected", treating the dropdown as a reset keeps one simple rule —
  // pick an environment, everything it can run is checked.
  useEffect(() => {
    setTestItems((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        const shouldSelect = !isExcludedByEnv(next[id]);
        if (next[id].selected !== shouldSelect) {
          next[id] = { ...next[id], selected: shouldSelect };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment]);

  const selectedIds = Object.keys(testItems).filter(
    (id) => testItems[id].selected && !isExcludedByEnv(testItems[id])
  );

  const toggleSelection = (id) => {
    setTestItems((prev) => ({ ...prev, [id]: { ...prev[id], selected: !prev[id].selected } }));
  };

  const toggleAllOfKind = (kind) => {
    setTestItems((prev) => {
      const ids = Object.keys(prev).filter((id) => prev[id].kind === kind && !isExcludedByEnv(prev[id]));
      const allSelected = ids.length > 0 && ids.every((id) => prev[id].selected);
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = { ...next[id], selected: !allSelected };
      });
      return next;
    });
  };

  const toggleExpanded = (id) => {
    setTestItems((prev) => ({ ...prev, [id]: { ...prev[id], expanded: !prev[id].expanded } }));
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    });
  };

  // ── Shared SSE line reader ───────────────────────────────────────────────
  const readSSEStream = async (response, onEvent) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();
      for (const block of parts) {
        if (!block.startsWith('data: ')) continue;
        try {
          onEvent(JSON.parse(block.slice(6)));
        } catch (e) {
          // ignore malformed SSE chunk
        }
      }
    }
  };

  const streamFinished = () => {
    activeStreamCount.current = Math.max(0, activeStreamCount.current - 1);
    if (activeStreamCount.current === 0) setIsRunning(false);
  };

  // ── E2E (Playwright) reporter-event handling — unchanged logic, now keyed
  // by the namespaced id so it shares state with Jest items. ────────────────
  const handleE2EReporterEvent = (event) => {
    const id = namespacedId('e2e', event.file);
    if (event.event === 'testBegin') {
      setTestItems((prev) => {
        const existing = prev[id];
        if (!existing) return prev;
        return {
          ...prev,
          [id]: {
            ...existing,
            status: 'running',
            expanded: true,
            childTests: existing.childTests.map((ct) => (ct.title === event.title ? { ...ct, status: 'running' } : ct)),
          },
        };
      });
      setStats((s) => ({ ...s, running: s.running + 1 }));
    } else if (event.event === 'testEnd') {
      const isFailure = event.status !== 'passed' && event.status !== 'skipped';
      setTestItems((prev) => {
        const existing = prev[id];
        if (!existing) return prev;
        const previousWasFailing = existing.status === 'failed' || existing.status === 'timedOut';
        const newStatus = previousWasFailing ? existing.status : event.status;
        const updated = existing.childTests.map((ct) =>
          ct.title === event.title
            ? { ...ct, status: event.status, traceUrl: event.traceUrl, videoUrl: event.videoUrl, error: event.error }
            : ct
        );
        if (!updated.some((ct) => ct.title === event.title)) {
          updated.push({ title: event.title, status: event.status, traceUrl: event.traceUrl, videoUrl: event.videoUrl, error: event.error });
        }
        return {
          ...prev,
          [id]: {
            ...existing,
            status: newStatus,
            error: isFailure ? existing.error || event.error : existing.error,
            totalTests: (existing.totalTests || 0) + 1,
            passedTests: (existing.passedTests || 0) + (event.status === 'passed' ? 1 : 0),
            failedTests: (existing.failedTests || 0) + (isFailure ? 1 : 0),
            childTests: updated,
          },
        };
      });
      setStats((s) => ({
        ...s,
        running: Math.max(0, s.running - 1),
        passed: event.status === 'passed' ? s.passed + 1 : s.passed,
        failed: isFailure ? s.failed + 1 : s.failed,
      }));
    }
  };

  // ── Jest reporter-event handling — new, structured (see scripts/jestSseReporter.js) ──
  const handleJestReporterEvent = (event) => {
    if (event.event === 'fileBegin') {
      const id = namespacedId('jest', event.file);
      setTestItems((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], status: 'running', expanded: true } } : prev));
    } else if (event.event === 'testEnd') {
      const id = namespacedId('jest', event.file);
      const isFailure = event.status === 'failed';
      setTestItems((prev) => {
        const existing = prev[id];
        if (!existing) return prev;
        const updated = [...existing.childTests];
        const idx = updated.findIndex((ct) => ct.title === event.title);
        const childEntry = { title: event.title, status: event.status, error: event.error, reproduceCommand: event.reproduceCommand, expanded: false };
        if (idx >= 0) updated[idx] = { ...updated[idx], ...childEntry };
        else updated.push(childEntry);
        return {
          ...prev,
          [id]: {
            ...existing,
            totalTests: (existing.totalTests || 0) + 1,
            passedTests: (existing.passedTests || 0) + (event.status === 'passed' ? 1 : 0),
            failedTests: (existing.failedTests || 0) + (isFailure ? 1 : 0),
            childTests: updated,
          },
        };
      });
      if (event.status === 'passed' || isFailure) {
        setStats((s) => ({ ...s, passed: event.status === 'passed' ? s.passed + 1 : s.passed, failed: isFailure ? s.failed + 1 : s.failed }));
      }
    } else if (event.event === 'fileEnd') {
      const id = namespacedId('jest', event.file);
      setTestItems((prev) => {
        const existing = prev[id];
        if (!existing) return prev;
        return { ...prev, [id]: { ...existing, status: event.numFailingTests > 0 ? 'failed' : 'passed' } };
      });
    }
  };

  // ── Run ───────────────────────────────────────────────────────────────────
  const startE2EStream = async (files) => {
    activeStreamCount.current += 1;
    e2eAbortRef.current = new AbortController();
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL || '';
    try {
      const response = await fetch(
        `${apiUrl}/api/internal/support-requests/tests/stream?files=${encodeURIComponent(files.join(','))}&targetEnv=${encodeURIComponent(environment)}`,
        { headers: { Authorization: `Bearer ${token}` }, signal: e2eAbortRef.current.signal }
      );
      if (!response.ok) throw new Error('Failed to start E2E test stream');
      await readSSEStream(response, (parsed) => {
        if (parsed.type === 'log') {
          try {
            const msgObj = JSON.parse(parsed.message);
            if (msgObj.type === 'reporter-event') handleE2EReporterEvent(msgObj);
          } catch (e) {
            /* plain-text log line, not JSON — ignore */
          }
        } else if (parsed.type === 'error') {
          setLoadError(parsed.message);
        }
      });
    } catch (err) {
      console.error(err);
    } finally {
      streamFinished();
    }
  };

  const startJestStream = async (files) => {
    activeStreamCount.current += 1;
    jestAbortRef.current = new AbortController();
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL || '';
    try {
      const response = await fetch(
        `${apiUrl}/api/internal/support-requests/jest-tests/stream?files=${encodeURIComponent(files.join(','))}`,
        { headers: { Authorization: `Bearer ${token}` }, signal: jestAbortRef.current.signal }
      );
      if (!response.ok) throw new Error('Failed to start Jest test stream');
      await readSSEStream(response, (parsed) => {
        if (parsed.type === 'log') {
          try {
            const msgObj = JSON.parse(parsed.message);
            if (msgObj.type === 'reporter-event') handleJestReporterEvent(msgObj);
          } catch (e) {
            /* Jest's normal human-readable output line — not a reporter event */
          }
        }
      });
    } catch (err) {
      console.error(err);
    } finally {
      streamFinished();
    }
  };

  const runSelected = () => {
    const jestFiles = selectedIds.filter((id) => testItems[id].kind === 'jest').map((id) => testItems[id].file);
    const e2eFiles = selectedIds.filter((id) => testItems[id].kind === 'e2e').map((id) => testItems[id].file);
    if (jestFiles.length === 0 && e2eFiles.length === 0) return;

    setIsRunning(true);
    setStats({ passed: 0, failed: 0, running: 0 });
    setTestItems((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[id] = {
          ...next[id],
          status: 'idle',
          error: null,
          totalTests: 0,
          passedTests: 0,
          failedTests: 0,
          childTests:
            next[id].kind === 'e2e'
              ? next[id].childTests.map((ct) => ({ ...ct, status: 'idle', error: null, traceUrl: null, videoUrl: null }))
              : [],
        };
      });
      return next;
    });

    if (jestFiles.length > 0) startJestStream(jestFiles);
    if (e2eFiles.length > 0) startE2EStream(e2eFiles);
  };

  const stopAll = () => {
    if (e2eAbortRef.current) e2eAbortRef.current.abort();
    if (jestAbortRef.current) jestAbortRef.current.abort();
    activeStreamCount.current = 0;
    setIsRunning(false);
  };

  // ── Retest a single test (either kind) ───────────────────────────────────
  const retestSingle = async (id, testTitle) => {
    const item = testItems[id];
    const key = `${id}::${testTitle}`;
    setTestItems((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        childTests: prev[id].childTests.map((ct) => (ct.title === testTitle ? { ...ct, status: 'running', error: null } : ct)),
      },
    }));

    const controller = new AbortController();
    retestAbortControllers.current[key] = controller;
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const endpoint = item.kind === 'e2e' ? 'tests' : 'jest-tests';
    const envParam = item.kind === 'e2e' ? `&targetEnv=${encodeURIComponent(environment)}` : '';

    try {
      const url = `${apiUrl}/api/internal/support-requests/${endpoint}/stream?file=${encodeURIComponent(item.file)}&testTitle=${encodeURIComponent(testTitle)}${envParam}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
      if (!response.ok) throw new Error('Failed to start retest stream');
      await readSSEStream(response, (parsed) => {
        if (parsed.type !== 'log') return;
        try {
          const msgObj = JSON.parse(parsed.message);
          if (msgObj.type !== 'reporter-event') return;
          if (item.kind === 'e2e') handleE2EReporterEvent(msgObj);
          else handleJestReporterEvent(msgObj);
        } catch (e) {
          /* ignore */
        }
      });
    } catch (err) {
      console.error(err);
      setTestItems((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          childTests: prev[id].childTests.map((ct) => (ct.title === testTitle ? { ...ct, status: 'failed', error: err.message } : ct)),
        },
      }));
    }
  };

  // ── Visible lists + the one-line summary of what's hidden ────────────────
  // Counts are of FILES, not individual tests — the list endpoints return files.
  // (The old banners said "tests", which overstated them.)
  const visibleIds = (kind) =>
    Object.keys(testItems).filter((id) => testItems[id].kind === kind && !isExcludedByEnv(testItems[id]));
  const hiddenCount = (kind) =>
    Object.values(testItems).filter((it) => it.kind === kind && isExcludedByEnv(it)).length;

  const jestIds = visibleIds('jest');
  const e2eIds = visibleIds('e2e');
  const jestHidden = hiddenCount('jest');
  const e2eHidden = hiddenCount('e2e');

  const envNotes = [
    jestHidden > 0 &&
      `${jestHidden} server (Jest) file${jestHidden === 1 ? '' : 's'} hidden — Jest boots its own in-memory database, so it only runs on Localhost.`,
    e2eHidden > 0 &&
      `${e2eHidden} E2E file${e2eHidden === 1 ? '' : 's'} hidden — only specs certified read-only (or self-cleaning) run against Prod. Enforced on the server too.`,
  ].filter(Boolean);

  return (
    <div className="p-6 bg-slate-50 min-h-full h-full flex flex-col">
      <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Run Tests</h1>
          <p className="text-slate-500 text-sm">
            One environment, one button — what runs is chosen for you based on what's safe there.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 px-3 py-2 rounded-xl shadow-sm">
            Environment:
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              className="bg-transparent border-none p-0 focus:ring-0 text-slate-800 font-semibold cursor-pointer outline-none"
              disabled={isRunning}
            >
              {Object.entries(ENV_RULES).map(([key, rule]) => (
                <option key={key} value={key}>
                  {rule.label}
                </option>
              ))}
            </select>
          </div>
          {!isRunning ? (
            <button
              onClick={runSelected}
              disabled={selectedIds.length === 0}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-colors"
            >
              <Play className="w-4 h-4" /> Run Selected ({selectedIds.length})
            </button>
          ) : (
            <button
              onClick={stopAll}
              className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-colors"
            >
              <StopCircle className="w-4 h-4" /> Stop
            </button>
          )}
        </div>
      </div>

      {envNotes.length > 0 && (
        <div className="mb-4 flex items-start gap-2 text-xs text-slate-500 px-1">
          <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            {envNotes.map((note) => (
              <p key={note}>{note}</p>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-center">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Status</p>
          <div className="flex items-center gap-2">
            {isRunning ? (
              <>
                <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
                <span className="text-lg font-bold text-slate-700">Running...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span className="text-lg font-bold text-slate-700">Idle</span>
              </>
            )}
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-center">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Passed</p>
          <span className="text-2xl font-bold text-emerald-600">{stats.passed}</span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-center">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Failed</p>
          <span className="text-2xl font-bold text-red-500">{stats.failed}</span>
        </div>
      </div>

      {loadError && (
        <div className="mb-4 bg-red-50 text-red-600 p-4 rounded-xl border border-red-200 font-medium flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span>{loadError}</span>
        </div>
      )}

      <div className="space-y-6 flex-1 overflow-y-auto">
        <TestGroup
          title="Server — Unit & Integration (Jest)"
          ids={jestIds}
          testItems={testItems}
          isRunning={isRunning}
          toggleAll={() => toggleAllOfKind('jest')}
          toggleSelection={toggleSelection}
          toggleExpanded={toggleExpanded}
          retestSingle={retestSingle}
          copyToClipboard={copyToClipboard}
          copiedKey={copiedKey}
        />
        <TestGroup
          title="Browser — E2E (Playwright)"
          ids={e2eIds}
          testItems={testItems}
          isRunning={isRunning}
          toggleAll={() => toggleAllOfKind('e2e')}
          toggleSelection={toggleSelection}
          toggleExpanded={toggleExpanded}
          retestSingle={retestSingle}
          copyToClipboard={copyToClipboard}
          copiedKey={copiedKey}
        />
      </div>

      {!availableTestsLoaded && Object.keys(testItems).length === 0 && (
        <div className="text-sm text-slate-400 mt-4">Loading test lists…</div>
      )}
      {availableTestsLoaded && jestIds.length === 0 && e2eIds.length === 0 && (
        <div className="text-sm text-slate-400 mt-4">
          Nothing can run against {ENV_RULES[environment].label}. Switch to Localhost to run the full suite.
        </div>
      )}
    </div>
  );
};

// ── One card-list renderer shared by both groups ──────────────────────────
// `ids` is already filtered to what the selected environment can run, so this
// component has no notion of a disabled row — it renders nothing it can't run.
function TestGroup({
  title,
  ids,
  testItems,
  isRunning,
  toggleAll,
  toggleSelection,
  toggleExpanded,
  retestSingle,
  copyToClipboard,
  copiedKey,
}) {
  if (ids.length === 0) return null;
  const allSelected = ids.every((id) => testItems[id].selected);

  return (
    <div>
      <div className="flex items-center gap-2 px-1 mb-2">
        <input
          type="checkbox"
          className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
          checked={allSelected}
          onChange={toggleAll}
          disabled={isRunning}
        />
        <span className="text-sm font-semibold text-slate-700">
          {title} <span className="text-slate-400 font-normal">({ids.length} files)</span>
        </span>
      </div>

      <div className="space-y-2">
        {ids.map((id) => {
          const item = testItems[id];
          return (
            <div
              key={id}
              className={`bg-white rounded-xl border overflow-hidden transition-all ${
                item.selected ? 'border-emerald-200 shadow-sm' : 'border-slate-200 opacity-60'
              }`}
            >
              <div className="px-5 py-3 flex justify-between items-center bg-slate-50">
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    onClick={() => toggleExpanded(id)}
                    className="text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                  >
                    {item.expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer flex-shrink-0"
                    checked={item.selected}
                    onChange={() => toggleSelection(id)}
                    disabled={isRunning}
                  />
                  {item.status === 'idle' && <CheckCircle className="w-5 h-5 text-slate-300 flex-shrink-0" />}
                  {item.status === 'running' && <RefreshCw className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" />}
                  {item.status === 'passed' && <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
                  {(item.status === 'failed' || item.status === 'timedOut') && (
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  )}
                  <h3 className={`font-mono text-sm font-semibold truncate ${item.selected ? 'text-slate-700' : 'text-slate-500'}`}>
                    {item.file}
                  </h3>
                  {item.totalTests > 0 && (
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                        item.failedTests > 0
                          ? 'bg-red-100 text-red-600'
                          : item.status === 'running'
                          ? 'bg-blue-100 text-blue-600'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {item.failedTests > 0
                        ? `${item.failedTests} failed / ${item.totalTests}`
                        : item.status === 'running'
                        ? `${item.passedTests || 0} / ${item.totalTests} done`
                        : `${item.passedTests} / ${item.totalTests} passed`}
                    </span>
                  )}
                </div>
              </div>

              {item.expanded && item.childTests && item.childTests.length > 0 && (
                <div className="border-t border-slate-100 bg-white">
                  {item.childTests.map((ct, idx) => {
                    const isCtFailure = ct.status === 'failed' || ct.status === 'timedOut';
                    const reproKey = `${id}::${ct.title}`;
                    return (
                      <div key={idx} className="border-b border-slate-50 last:border-b-0">
                        <div className="px-8 py-3 flex justify-between items-center hover:bg-slate-50">
                          <div className="flex items-center gap-3 min-w-0">
                            {ct.status === 'idle' && <div className="w-2 h-2 rounded-full bg-slate-300 ml-1.5 mr-1 flex-shrink-0" />}
                            {ct.status === 'running' && <RefreshCw className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />}
                            {ct.status === 'passed' && <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                            {isCtFailure && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                            <span className="text-sm text-slate-600 font-medium truncate">{ct.title}</span>
                          </div>
                          <div className="flex gap-3 flex-shrink-0">
                            {ct.traceUrl && (
                              <a
                                href={`https://trace.playwright.dev/?trace=${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${ct.traceUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs bg-indigo-50 text-indigo-600 px-3 py-1.5 rounded font-medium hover:bg-indigo-100 transition"
                              >
                                View Trace
                              </a>
                            )}
                            {ct.videoUrl && (
                              <a
                                href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${ct.videoUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs bg-indigo-50 text-indigo-600 px-3 py-1.5 rounded font-medium hover:bg-indigo-100 transition"
                              >
                                View Video
                              </a>
                            )}
                            {ct.reproduceCommand && (
                              <button
                                onClick={() => copyToClipboard(ct.reproduceCommand, reproKey)}
                                className="text-xs bg-indigo-50 text-indigo-600 px-3 py-1.5 rounded font-medium hover:bg-indigo-100 transition flex items-center gap-1"
                              >
                                {copiedKey === reproKey ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                {copiedKey === reproKey ? 'Copied' : 'Copy reproduce command'}
                              </button>
                            )}
                            {isCtFailure && (
                              <button
                                onClick={() => retestSingle(id, ct.title)}
                                className="text-xs bg-slate-200 text-slate-700 px-3 py-1.5 rounded font-medium hover:bg-slate-300 transition flex items-center gap-1"
                              >
                                <RefreshCw className="w-3 h-3" /> Retest
                              </button>
                            )}
                          </div>
                        </div>
                        {isCtFailure && ct.error && (
                          <div className="mx-8 mb-3 p-3 bg-red-50 border border-red-100 rounded-lg text-xs font-mono text-red-700 whitespace-pre-wrap overflow-x-auto">
                            {ct.error}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {item.error && !item.childTests.some((ct) => ct.error) && (
                <div className="p-4 bg-red-50 border-t border-red-100 text-xs font-mono text-red-600 whitespace-pre-wrap">
                  {item.error}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AIHealerAdmin;
